import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch, ControllerRenderProps } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Switch } from '@/components/shadcn/switch'
import { Textarea } from '@/components/shadcn/textarea'
import { FormImagePreview } from '@/components/form-components/FormImagePreview'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'

// Form validation schema
const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name cannot exceed 100 characters'),
  longName: z.string().max(128, 'Long name cannot exceed 128 characters').optional(),
  description: z.string().max(256, 'Description cannot exceed 256 characters').optional(),
  active: z.boolean(),
  domain: z.string().max(128, 'Domain cannot exceed 128 characters').optional(),
  coBrandingImageKey: z.string().optional(),
  primary: z.string().regex(hexRegex, 'Invalid hex code').optional().or(z.literal('')),
  secondary: z.string().regex(hexRegex, 'Invalid hex code').optional().or(z.literal('')),
  accent: z.string().regex(hexRegex, 'Invalid hex code').optional().or(z.literal('')),
})

export type OrganisationForm = z.infer<typeof formSchema>

type MessagesSubscriptionsOrganisationSummary = RestSchemas['Messages.Subscriptions.OrganisationSummary']
type MessagesApplicationsPictogram = RestSchemas['Messages.Applications.Pictogram']

type OrganisationDetailsFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedOrg?: MessagesSubscriptionsOrganisationSummary
  pictograms: MessagesApplicationsPictogram[]
}

export const OrganisationDetailsForm = (props: Omit<OrganisationDetailsFormProps, 'pictograms'>) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedOrg
  const {
    getApplicationsApiGetPictograms,
    getSubscriptionsApiGetOrganisations,
    getSubscriptionsApiGetOrganisation,
    putSubscriptionsApiAddOrganisation,
    postSubscriptionsApiUpdateOrganisation,
  } = useRestClient()

  const {
    data: pictograms,
    isPending: pictogramsLoading,
    isError: pictogramsError,
  } = getApplicationsApiGetPictograms.useQuery()

  const { mutate: updateOrganisation } = postSubscriptionsApiUpdateOrganisation.useMutation(undefined, {
    onSuccess: () => {
      getSubscriptionsApiGetOrganisations.invalidateQueries({})
      getSubscriptionsApiGetOrganisation.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Organisation successfully updated')
    },
  })
  const { mutate: addOrganisation } = putSubscriptionsApiAddOrganisation.useMutation(undefined, {
    onSuccess: () => {
      getSubscriptionsApiGetOrganisations.invalidateQueries({})
      getSubscriptionsApiGetOrganisation.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Organisation successfully added')
    },
  })

  const submitHandler = useCallback(
    (data: OrganisationForm) => {
      const theme =
        !data.primary && !data.secondary && !data.accent
          ? '{}'
          : JSON.stringify({
              primary: data.primary,
              secondary: data.secondary,
              accent: data.accent,
            })

      if (edit && props.selectedOrg) {
        updateOrganisation({
          body: {
            id: props.selectedOrg.id,
            name: data.name,
            longName: data.longName || '',
            description: data.description || '',
            whitelistedDomain: data.domain || '',
            active: data.active,
            coBrandingImageKey: data.coBrandingImageKey || '',
            theme,
          },
        })
      } else {
        addOrganisation({
          body: {
            name: data.name,
            longName: data.longName || '',
            description: data.description || '',
            whitelistedDomain: data.domain || '',
            coBrandingImageKey: data.coBrandingImageKey || '',
            theme,
          },
        })
      }
    },
    [edit, props.selectedOrg, addOrganisation, updateOrganisation]
  )

  if (pictogramsLoading) return <FormComponentLoading />
  if (pictogramsError) return <ErrorComponent />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} pictograms={pictograms} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} pictograms={pictograms} submitHandler={submitHandler} />
  )
}

const FormComponent = (props: OrganisationDetailsFormProps & { submitHandler: (data: OrganisationForm) => void }) => {
  const selectedOrg: Partial<MessagesSubscriptionsOrganisationSummary> & { theme?: string | null } =
    props.selectedOrg ?? {}
  const { name, longName, description, whitelistedDomain, active, coBrandingImageKey, theme } = selectedOrg

  const defaultValues = useMemo(() => {
    let parsedTheme = { primary: '', secondary: '', accent: '' }
    try {
      if (theme) {
        const parsed = JSON.parse(theme)
        parsedTheme = {
          primary: parsed.primary ?? '',
          secondary: parsed.secondary ?? '',
          accent: parsed.accent ?? '',
        }
      }
    } catch (e) {
      console.error('Failed to parse theme JSON', e)
    }

    return {
      name: name ?? '',
      longName: longName ?? '',
      description: description ?? '',
      domain: whitelistedDomain ?? '',
      active: active != null ? active : true,
      coBrandingImageKey: coBrandingImageKey ?? '',
      primary: parsedTheme.primary,
      secondary: parsedTheme.secondary,
      accent: parsedTheme.accent,
    }
  }, [active, coBrandingImageKey, description, longName, name, theme, whitelistedDomain])

  const form = useForm<OrganisationForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const watchCoBrandingImageKey = useWatch({
    control: form.control,
    name: 'coBrandingImageKey',
  })

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            {/* <div className="form-wrapper"> */}
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="longName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Long name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="domain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Domain</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="coBrandingImageKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cobranding Image</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an image" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {props.pictograms.map((p) => (
                          <SelectItem key={p.imageKey} value={p.imageKey}>
                            {p.imageKey}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormImagePreview imageKey={watchCoBrandingImageKey} />
            </div>
            <div className="mt-10 space-y-4 rounded-lg bg-foreground/5 p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Colour theme</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    form.setValue('primary', '')
                    form.setValue('secondary', '')
                    form.setValue('accent', '')
                  }}
                >
                  Clear all
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="primary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary colour</FormLabel>
                      <FormControl>
                        <ColorInput field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="secondary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Colour</FormLabel>
                      <FormControl>
                        <ColorInput field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accent colour</FormLabel>
                      <FormControl>
                        <ColorInput field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="form-action-container">
              <Button type="submit">Submit</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}

const ColorInput = ({
  field,
}: {
  field: ControllerRenderProps<OrganisationForm, 'primary' | 'secondary' | 'accent'>
}) => {
  return (
    <div className="relative">
      <Input {...field} className="pl-10" placeholder="i.e #000000" />
      <div
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 overflow-hidden rounded-sm border shadow-sm"
        style={{ backgroundColor: field.value || '#ffffff' }}
      >
        <input
          type="color"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          value={field.value?.length === 7 ? field.value : undefined}
          onChange={(e) => field.onChange(e.target.value)}
        />
      </div>
    </div>
  )
}
